import { Label } from '@/components/ui-kit/label';
import { resolveTemplateVariables } from '../utils';

import { cn } from '@/lib/utils';
import { AISuggestionStatusBadge } from './AISuggestionStatusBadge';
import { FieldRendererProps } from '../types';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui-kit/badge';
import { AlertTriangle, Check } from 'lucide-react';

/**
 * Multi-Select Field Renderer
 * Redesigned with checkbox-style UI for clear multi-selection indication
 */
export function MultiSelectField({
  field,
  value,
  onChange,
  error,
  enrichmentContext,
  formData,
}: FieldRendererProps) {
  const showSuggestion = field.aiSuggestionEnabled && field.aiSuggestionKey;

  // Resolve template variables
  const resolvedLabel = resolveTemplateVariables(field.label, formData, enrichmentContext);

  const resolvedHelpText = resolveTemplateVariables(field.helpText, formData, enrichmentContext);

  // Ensure value is an array
  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

  // Check if we have option descriptions from enrichment context
  const getOptionLabel = (option: string) => {
    const optionKey = `${field.name}_${option}_label`;
    if (enrichmentContext && optionKey in enrichmentContext) {
      // Apply variable interpolation to enrichment label
      return resolveTemplateVariables(
        String(enrichmentContext[optionKey]),
        formData,
        enrichmentContext
      );
    }
    // Apply variable interpolation to raw option
    return resolveTemplateVariables(option, formData, enrichmentContext);
  };

  const getOptionDescription = (option: string) => {
    const optionKey = `${field.name}_${option}_description`;
    if (enrichmentContext && optionKey in enrichmentContext) {
      // Apply variable interpolation to enrichment description
      const description = resolveTemplateVariables(
        String(enrichmentContext[optionKey]),
        formData,
        enrichmentContext
      );
      return description || null;
    }
    return null;
  };

  // Toggle option selection
  const toggleOption = (option: string) => {
    const newValues = selectedValues.includes(option)
      ? selectedValues.filter((v) => v !== option)
      : [...selectedValues, option];
    onChange(field.name, newValues);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="min-w-0">
            <div className="flex items-start justify-between gap-2">
              <Label className="text-base font-semibold font-nunito text-primary-light">
                {resolvedLabel}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {showSuggestion && (
                <AISuggestionStatusBadge
                  suggestionKey={field.aiSuggestionKey as string}
                  enrichmentContext={enrichmentContext}
                  currentValue={selectedValues}
                  className=" md:hidden mt-1"
                />
              )}
            </div>
            {!resolvedHelpText && <p className="text-sm text-secondary-300">{resolvedHelpText} </p>}
          </div>
          {selectedValues.length > 0 && (
            <Badge
              variant="secondary"
              className="text-xs bg-selise-blue-500/10 text-selise-blue-500 border-selise-blue-500/20"
            >
              {selectedValues.length} selected
            </Badge>
          )}
        </div>
        {showSuggestion && (
          <AISuggestionStatusBadge
            suggestionKey={field.aiSuggestionKey as string}
            enrichmentContext={enrichmentContext}
            currentValue={selectedValues}
            className="hidden md:flex"
          />
        )}
      </div>

      {/* Checkbox-style multi-select options */}
      <div className="space-y-2">
        {field.options.map((option) => {
          const isSelected = selectedValues.includes(option);
          const optionLabel = getOptionLabel(option);
          const optionDescription = getOptionDescription(option);

          return (
            <motion.label
              key={option}
              htmlFor={`${field.name}-${option}`}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className={cn(
                'flex items-center gap-3 rounded-xl cursor-pointer transition-all group',
                '',
                isSelected ? 'border-selise-blue-500 ' : 'border-secondary-100 bg-background',
                error && 'border-amber-400 bg-amber-50/50'
              )}
            >
              {/* Custom checkbox */}
              <div className="relative shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  id={`${field.name}-${option}`}
                  checked={isSelected}
                  onChange={() => toggleOption(option)}
                  className="sr-only"
                />
                <div
                  className={cn(
                    'w-4.5 h-4.5 rounded border-2 flex items-center justify-center transition-all',
                    isSelected
                      ? 'bg-selise-blue-500 border-selise-blue-500'
                      : 'bg-background border-secondary-100'
                  )}
                >
                  {isSelected && <Check className="w-3.5 h-3.5 text-white stroke-3" />}
                </div>
              </div>

              {/* Option content */}
              <div className="flex-1 min-w-0">
                <div
                  className={cn(
                    'font-semibold text-sm transition-colors font-nunito',
                    isSelected ? 'text-selise-blue-500' : 'text-secondary-400'
                  )}
                >
                  {optionLabel}
                </div>
                {optionDescription && (
                  <div className="text-sm text-globe-grey mt-1 leading-relaxed">
                    {optionDescription}
                  </div>
                )}
              </div>
            </motion.label>
          );
        })}
      </div>

      {/* Selected items summary (if any selected) */}
      {selectedValues.length > 0 && (
        <div className="pt-2 border-t border-border">
          <p className="text-sm font-semibold font-nunito text-secondary-400 mb-2">Selected:</p>
          <div className="flex flex-wrap gap-2">
            {selectedValues.map((val) => {
              const label = getOptionLabel(val);
              return (
                <Badge
                  key={val}
                  variant="secondary"
                  className="text-xs bg-selise-blue-500/10 text-selise-blue-500 border-selise-blue-500/20 hover:bg-selise-blue-500/20"
                >
                  <Check className="w-3 h-3 mr-1" />
                  {label}
                </Badge>
              );
            })}
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 mt-2 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="font-medium">{error}</span>
        </div>
      )}
    </div>
  );
}
